/**
 * What approving a change request will do
 * (`GET /api/v1/change-requests/:id/approval-preview`): a dry run on the server
 * that writes nothing. The key sits under the item's detail key, so an in-place
 * review edit (which invalidates the detail) also refreshes the preview.
 */

import { useCallback } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { useAuth } from '@clerk/clerk-react';

import { ChangeRequestsService } from '@/api/services/ChangeRequestsService';
import type { change_requests_ApprovalPreviewResponse } from '@/api/models/change_requests_ApprovalPreviewResponse';
import { useAuthContext } from '@/contexts/AuthContext';
import { workflowKeys } from './useWorkflows';

export const approvalPreviewKey = (id: string, orgId: string) =>
  [...workflowKeys.detail(id), 'approval-preview', orgId] as const;

export function useApprovalPreview(itemId: string | undefined, enabled: boolean) {
  const { isSignedIn, orgId } = useAuth();
  const { isAuthReady } = useAuthContext();
  return useQuery({
    queryKey: approvalPreviewKey(itemId ?? '', orgId ?? ''),
    queryFn: () => ChangeRequestsService.getApiV1ChangeRequestsApprovalPreview(itemId ?? ''),
    enabled: enabled && !!itemId && !!isSignedIn && isAuthReady && !!orgId,
    // The preview reads live data (bookings, capacity); refresh it on every open.
    staleTime: 0,
    retry: false,
  });
}

/**
 * The same preview as a one-off read, for "Approve all": the batch checks each
 * request before it approves anything. Shares the key with the sheet's query,
 * and fetches fresh every time because bookings and capacity move.
 */
export function useFetchApprovalPreview(): (
  itemId: string,
) => Promise<change_requests_ApprovalPreviewResponse> {
  const queryClient = useQueryClient();
  const { orgId } = useAuth();
  return useCallback(
    (itemId: string) =>
      queryClient.fetchQuery({
        queryKey: approvalPreviewKey(itemId, orgId ?? ''),
        queryFn: () => ChangeRequestsService.getApiV1ChangeRequestsApprovalPreview(itemId),
        staleTime: 0,
        retry: 1,
      }),
    [queryClient, orgId],
  );
}
